import { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";

/**
 * The caller's enquiry ownership policy ("Enquiry Ownership Control").
 *
 * When the institute Super Admin turns the setting on, a regular admin sees
 * only the leads assigned to them, cannot pick an assignee, and cannot delete.
 * The backend enforces all three; this hook exists so the UI stops *offering*
 * the two actions it would refuse — an assignee dropdown that silently ignores
 * your choice and a Delete that 403s are both worse than no control at all.
 *
 * Cached module-wide like `useEnquiryStages`: the answer is one boolean pair
 * per session and every enquiry screen wants it, so refetching per mount would
 * be a request per navigation for data that cannot change without a settings
 * edit. `invalidateEnquiryOwnership()` drops it after that edit.
 *
 * Fails **closed** on error: an unreachable policy endpoint hides assign and
 * delete rather than showing controls that may not be permitted. Nothing is
 * lost — a Super Admin gets them back on the next successful load, and the
 * server is the one that decides either way.
 *
 * `unresolved` is the one answer that is neither "allowed" nor "denied": the
 * caller is restricted to their own enquiries and the server cannot work out
 * which admin they are, so the ownership filter matches nothing and every list
 * they open is empty for ever. It is reported so a screen can say that instead
 * of rendering a convincing empty pipeline — see EnquiriesList.
 */

let _cache = null;
let _inFlight = null;

const DENIED = {
  restricted: true,
  isSuperAdmin: false,
  canAssign: false,
  canDelete: false,
  ownAdminId: null,
  /* ⚠️ FALSE here, not `true`, even though `ownAdminId` is null.
     `unresolved` means "the server looked and could not find your Admin
     record"; this object is what a caller holds BEFORE the server has been
     asked, and while a request is in flight. Deriving it from `ownAdminId`
     would make every screen flash "we cannot identify you" on first paint. */
  unresolved: false,
};

/** Drop the cache after the ownership setting is toggled. */
export function invalidateEnquiryOwnership() {
  _cache = null;
  _inFlight = null;
}

async function fetchOwnership() {
  if (_cache) return _cache;
  if (_inFlight) return _inFlight;

  _inFlight = apiClient
    .get("/enquiries/ownership")
    .then(({ data }) => {
      _cache = data?.ownership || DENIED;
      return _cache;
    })
    .catch(() => DENIED) // not cached — a transient failure must not stick
    .finally(() => { _inFlight = null; });

  return _inFlight;
}

/**
 * @param {object}  [opts]
 * @param {boolean} [opts.enabled=true] Set false on surfaces with no logged-in
 *   admin — the public `/enroll` form renders the same component, and firing an
 *   authenticated request from there would 401 straight into apiClient's
 *   auto-logout. Disabled means denied, which is what a public form wants.
 */
export default function useEnquiryOwnership({ enabled = true } = {}) {
  const [ownership, setOwnership] = useState(_cache || DENIED);
  const [loading, setLoading] = useState(enabled && !_cache);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchOwnership().then((result) => {
      if (!cancelled) {
        setOwnership(result);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [enabled]);

  return { ...ownership, loading };
}
